// geoVINUM product family registry.
// Each entry powers the dedicated product page (/atlas, /portfolio, ...) and the
// home-page hierarchy. Translations are looked up by key from i18n.jsx, with
// English fallback when a key is missing.

export const PILLARS = [
  { key: "explore", slug: "explore", products: ["atlas"] },
  { key: "discover", slug: "discover", products: ["portfolio"] },
  { key: "experience", slug: "experience", products: ["tasting"] },
  { key: "learn", slug: "learn", products: ["coach", "academy"] },
  { key: "collect", slug: "collect", products: ["maps"] },
  { key: "integrate", slug: "integrate", products: ["iframe"] },
];

// Visuals — real geoVINUM product mockups (local public assets).
const IMG = {
  home: "/images/home.jpg",
  atlas: "/images/cartes-interactives.jpg",
  atlas_mini: "/images/cartes-interactives.jpg",
  atlas_magnum: "/images/magnum.png",
  atlas_pro: "/images/pro.png",
  portfolio: "/images/portfolio.jpg",
  tasting: "/images/tasting.jpg",
  coach: "/images/coach.jpg",
  maps: "/images/educativos.jpg",
  iframe: "/images/iframe.jpg",
  academy: "/images/academy.jpg",
};

export const HOME_HERO_IMAGE = IMG.home;

export const PRODUCTS = {
  atlas: {
    slug: "atlas",
    emoji: "🗺️",
    pillar: "explore",
    type: "app",
    status: "available",
    image: IMG.atlas,
    versions: [
      {
        slug: "atlas-mini",
        platform: "Web App",
        price_label: "free",
        url: "https://app.geovinum.io",
        image: IMG.atlas_mini,
        positioning: "b2c",
        audience: ["beginners", "wine_lovers", "travellers", "casual_learners"],
        benefits: ["interactive_maps", "major_regions", "essential_terroir", "mobile_friendly"],
      },
      {
        slug: "atlas-magnum",
        platform: "Web App",
        price_label: "premium",
        url: "https://app.geovinum.io",
        image: IMG.atlas_magnum,
        positioning: "b2c",
        free_trial_days: 30,
        audience: ["wset_students", "sommeliers", "wine_pros", "advanced_enthusiasts"],
        benefits: ["detailed_maps", "expanded_appellations", "in_depth_terroir", "advanced_edu"],
      },
      {
        slug: "atlas-pro",
        platform: "Web App",
        price_label: "subscription",
        url: "https://app.geovinum.io",
        image: IMG.atlas_pro,
        positioning: "b2b",
        free_trial_days: 30,
        audience: ["importers", "distributors", "wine_merchants", "wine_educators", "consultants"],
        benefits: ["full_premium_maps", "pro_region_db", "advanced_insights", "business_ready"],
      },
    ],
  },
  portfolio: {
    slug: "portfolio",
    emoji: "🍷",
    pillar: "discover",
    type: "app",
    status: "coming_soon",
    image: IMG.portfolio,
    positioning: "b2b",
    audience: ["wineries", "wine_brands", "importers", "wine_tourism_ops"],
    benefits: ["qr_code", "interactive_pages", "terroir_storytelling", "customer_engagement"],
  },
  tasting: {
    slug: "tasting",
    emoji: "🍷",
    pillar: "experience",
    type: "app",
    status: "coming_soon",
    image: IMG.tasting,
    positioning: "b2b",
    audience: ["wineries", "wine_bars", "events", "hospitality"],
    benefits: ["guided_tasting", "educational_content", "mobile_first", "enhanced_interaction"],
  },
  coach: {
    slug: "coach",
    emoji: "🎓",
    pillar: "learn",
    type: "app",
    status: "coming_soon",
    image: IMG.coach,
    positioning: "education",
    audience: ["cert_students", "sommeliers", "sales_teams", "wine_lovers"],
    benefits: ["personalized", "instant_answers", "interactive_learning", "expertise_on_demand"],
    sections: {
      features: ["appellations", "certs", "grapes", "terroirs", "tasting", "instant"],
      examples: ["q1", "q2", "q3", "q4"],
      forWho: ["students", "sommeliers", "hospitality", "merchants", "enthusiasts"],
    },
    pricing: [
      {
        plan: "weekly",
        price: "€2.90",
        per: "week",
        badge: "flexible",
        features: ["learning_content", "quizzes", "personalized", "ai_coaching"],
      },
      {
        plan: "yearly",
        price: "€59.90",
        per: "year",
        equivalent: "€4.99/month",
        badge: "best_value",
        features: ["all_features", "premium_paths", "exclusive_content", "priority_updates"],
        highlighted: true,
      },
      {
        plan: "lifetime",
        price: "€99",
        per: "one_time",
        badge: "lifetime",
        features: ["permanent_access", "future_updates", "premium_content"],
      },
    ],
    trial: "7d",
  },
  maps: {
    slug: "maps",
    emoji: "🖼️",
    pillar: "collect",
    type: "physical",
    status: "available",
    price_from: "€29",
    image: IMG.maps,
    cta_to: "/maps",
    cta_external: false,
    positioning: "b2c",
    audience: ["wine_lovers", "educators", "tourism", "wine_pros"],
    benefits: ["interactive_exploration", "detailed_regions", "educational_content", "multi_device"],
  },
  iframe: {
    slug: "iframe",
    emoji: "🌍",
    pillar: "integrate",
    type: "platform",
    status: "available",
    image: IMG.iframe,
    url: "https://academy.geovinum.io/geovinum-iframe/",
    contact_sales: true,
    positioning: "b2b",
    audience: ["wineries", "wine_regions", "wine_schools", "tourism_orgs"],
    benefits: ["seamless_integration", "engagement_boost", "terroir_storytelling", "no_tech"],
    sections: {
      forWho: ["wineries", "regions", "tourism", "schools"],
      useCases: ["winery", "regional", "tourism", "education"],
      features: ["step1", "step2", "step3", "step4"],
    },
  },
  academy: {
    slug: "academy",
    emoji: "🎓",
    pillar: "learn",
    type: "service",
    status: "available",
    image: IMG.academy,
    cta_to: "/courses",
    contact_sales: true,
    positioning: "education",
    audience: ["wine_students", "hospitality_pros", "wine_schools", "casual_learners"],
    benefits: ["structured_paths", "interactive_maps", "regional_courses", "practical_knowledge"],
  },
  "atlas-magnum": {
    slug: "atlas-magnum",
    emoji: "🍷",
    pillar: "explore",
    type: "app",
    status: "available",
    image: IMG.atlas_magnum,
    positioning: "b2b",
    audience: ["wineries", "tourism_orgs", "wine_regions", "consultants"],
    benefits: ["detailed_maps", "expanded_appellations", "in_depth_terroir", "advanced_edu"],
    url: "https://app.geovinum.io",
    price_from: "490 € HT",
    trial: "7d",
    sections: {
      forWho: ["wineries", "tourism", "interprof", "consultants"],
      features: ["appellations", "cadastre", "custom", "share"],
      useCases: ["website", "fair", "export", "tourism"],
      examples: ["winery", "tourism", "interprof"],
      whyTitle: true,
    },
  },
  "atlas-pro": {
    slug: "atlas-pro",
    emoji: "🔬",
    pillar: "explore",
    type: "app",
    status: "available",
    image: IMG.atlas_pro,
    positioning: "b2b",
    audience: ["importers", "consultants", "wine_educators", "wine_pros"],
    benefits: ["full_premium_maps", "pro_region_db", "advanced_insights", "business_ready"],
    url: "https://app.geovinum.io",
    price_from: "29 € HT / mois",
    trial: "7d",
    sections: {
      forWho: ["importers", "consultants", "educators", "interprof"],
      features: ["terroir_data", "viz_3d", "mapping", "analysis"],
      useCases: ["import", "education", "consulting", "research"],
      examples: ["importer", "consultant", "educator"],
      whyTitle: true,
    },
  },
};

export const ALL_PRODUCT_SLUGS = Object.keys(PRODUCTS);
